import {
  QueryKey,
  QueryObserverOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import { TableQueryState } from 'components/Table/useTableQueryState';
import { queryClient } from 'query-client';
import { toast } from 'react-toastify';
import { CoreBaseConfiguration } from './BaseConfiguration';
import {
  SubtitleProfileCreateRequest,
  SubtitleProfileResponse,
  SubtitleProfileResponsePagedData,
  SubtitleProfilesApi,
  SubtitleProfileUpdateRequest,
} from './core';
import { MetaMappingsApiKeys } from './useMetaMappingsApi';

export const SubtitleProfileApiKeys = {
  SEARCH: 'subtitle-profiles-search',
  GET_MULTIPLE: 'subtitle-profiles',
  GET_SINGLE: 'subtitle-profile',
};

const client = new SubtitleProfilesApi(new CoreBaseConfiguration());

export const useSearchSubtitleProfiles = ({
  searchTerm,
}: {
  searchTerm?: string;
}) => {
  return useQuery({
    queryKey: [SubtitleProfileApiKeys.SEARCH, searchTerm],
    queryFn: () =>
      client.mediaOrbitSubtitleProfilesGet({
        size: 10,
        searchTerm,
      }),
    enabled: searchTerm !== undefined || searchTerm != '',
  });
};

export const useGetSubtitleProfiles = (
  state?: TableQueryState,
  options: Omit<
    QueryObserverOptions<
      SubtitleProfileResponsePagedData,
      Error,
      SubtitleProfileResponsePagedData,
      SubtitleProfileResponsePagedData,
      QueryKey
    >,
    'queryKey'
  > = {}
) => {
  return useQuery<SubtitleProfileResponsePagedData, Error>({
    queryKey: [SubtitleProfileApiKeys.GET_MULTIPLE, state, options],
    queryFn: () =>
      client.mediaOrbitSubtitleProfilesGet({
        pageNumber: state?.pagination.pageIndex,
        size: state?.pagination.pageSize,
        orderByProperty: state?.sorting[0]?.id || 'order',
        ascending: !state?.sorting?.length
          ? true
          : state?.sorting[0]?.desc === false,
        searchTerm: state?.globalFilter,
      }),
    ...options,
  });
};

export const useGetSubtitleProfile = (
  id?: string,
  options: Omit<
    QueryObserverOptions<
      SubtitleProfileResponse,
      Error,
      SubtitleProfileResponse,
      SubtitleProfileResponse,
      QueryKey
    >,
    'queryKey'
  > = {}
) => {
  return useQuery<SubtitleProfileResponse, Error>({
    queryKey: [SubtitleProfileApiKeys.GET_SINGLE, id],
    queryFn: async () => {
      const data = await client.mediaOrbitSubtitleProfilesIdGet({
        id: id ?? '',
      });
      return data;
    },
    enabled: id !== undefined,
    ...options,
  });
};

export const useCreateSubtitleProfile = () => {
  return useMutation({
    mutationFn: ({
      subtitleProfileCreateRequest,
    }: {
      subtitleProfileCreateRequest: SubtitleProfileCreateRequest;
    }) =>
      client.mediaOrbitSubtitleProfilesPost({
        subtitleProfileCreateRequest,
      }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [SubtitleProfileApiKeys.GET_MULTIPLE],
      });
      toast.success(`Undertekstprofilen '${e.name}' er blevet oprettet`, {
        toastId: 'createSubtitleProfile',
      });
    },
  });
};

export const useUpdateSubtitleProfile = () => {
  return useMutation({
    mutationFn: ({
      id,
      subtitleProfileUpdateRequest,
    }: {
      id: string;
      subtitleProfileUpdateRequest: SubtitleProfileUpdateRequest;
    }) =>
      client.mediaOrbitSubtitleProfilesIdPut({
        id,
        subtitleProfileUpdateRequest,
      }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [SubtitleProfileApiKeys.GET_MULTIPLE],
      });
      queryClient.invalidateQueries({
        queryKey: [SubtitleProfileApiKeys.GET_SINGLE, e.id],
      });
      queryClient.invalidateQueries({
        queryKey: [MetaMappingsApiKeys.GET_META_MAPPINGS, e.id],
      });
      toast.success(`Undertekstprofilen '${e.name}' er blevet opdateret`, {
        toastId: 'updateSubtitleProfile',
      });
    },
  });
};

export const useDeleteSubtitleProfile = () => {
  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      client.mediaOrbitSubtitleProfilesIdDelete({ id }),
    onSuccess: (e) => {
      queryClient.invalidateQueries({
        queryKey: [SubtitleProfileApiKeys.GET_MULTIPLE],
      });
      queryClient.invalidateQueries({
        queryKey: [SubtitleProfileApiKeys.GET_SINGLE, e.id],
      });
      toast.success(`Undertekstprofilen '${e.name}' er blevet arkiveret`, {
        toastId: 'deleteSubtitleProfile',
      });
    },
  });
};
